import i18next from 'i18next';
import { isValid, parse } from 'date-fns';
import { BalanceContextType, BalancesItem } from './context/BalanceContext';
import {
  IOnlineTourPriceResult,
  IOnlineTourSearchResult,
  TourParameter,
} from './utils/types';
import { adultAgeInMonths } from './constants';

declare global {
  interface Window {
    Android?: {
      handleAction: (action: string, data: string) => void;
    };
    webkit?: {
      messageHandlers?: {
        bridge: {
          postMessage: (message: { action: string; data: string }) => void;
        };
      };
    };
    receiveFromAndroid?: (action: string, data: string) => void;
    fromNative?: (action: string, data: any) => void;
  }
}

export function sendToNative(action: string, data: object | null) {
  const jsonData = JSON.stringify(data);
  // Android
  if (window.Android?.handleAction) {
    window.Android.handleAction(action, jsonData);
  }
  // iOS
  if (window.webkit?.messageHandlers?.bridge?.postMessage) {
    window.webkit.messageHandlers.bridge.postMessage({
      action,
      data: jsonData,
    });
  }
}

export const formatAmountInCurrency = (
  amount: number,
  currencyCode: string,
) => {
  if (!currencyCode) return formatAmount(amount);
  return amount.toLocaleString(i18next.language, {
    style: 'currency',
    maximumFractionDigits: 2,
    currency: currencyCode,
  });
};

export const hiddenPhoneChars = (tel: string) => {
  let updatedTel = '';
  for (let i = 0; i < tel.length; i++) {
    updatedTel += i > 4 && i < 11 ? '*' : tel[i];
  }
  return updatedTel;
};

export const passwordMatchCheck = (password: string, passwordAgain: string) => {
  return password === passwordAgain;
};
export const formatAmount = (amount: number, precision = 2) =>
  amount.toLocaleString(i18next.language, { maximumFractionDigits: precision });

export const currencyExchangeAmount = (amount: number, rate: number) =>
  amount * rate;

export function getTimeDiff(
  startDateString: string | undefined,
  startTimeString: string | undefined,
  endDateString: string | undefined,
  endTimeString: string | undefined,
) {
  const startDateTime = parse(
    `${startDateString}T${startTimeString}`,
    "yyyy-MM-dd'T'HH:mm:ss",
    new Date(),
  );
  const endDateTime = parse(
    `${endDateString}T${endTimeString}`,
    "yyyy-MM-dd'T'HH:mm:ss",
    new Date(),
  );

  if (!isValid(startDateTime) || !isValid(endDateTime)) {
    return null;
  }

  const timeDiff = endDateTime.getTime() - startDateTime.getTime();

  // Calculate hours, minutes, and seconds from the time difference
  const hours = Math.floor(timeDiff / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff / (1000 * 60)) % 60);

  return {
    hours,
    minutes,
  };
}

export const formattedDate = (date?: string) => {
  const dateOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };

  if (date) return new Date(date).toLocaleDateString('tr-TR', dateOptions);
  return '-';
};

export const formatDecimal = (value: number, precision = 2) =>
  parseFloat(value.toFixed(precision).replace(/\.?0+$/, ''));

export const withSelectedOrder = (
  balances: BalanceContextType,
  balancesSelection: string[],
) => {
  return balances.filter((balance) =>
    balancesSelection.includes(balance.orderNumber),
  );
};

export const withTotalPayment = (selected: BalancesItem[]) => {
  return {
    amount: +selected
      .reduce((acc, item) => acc + item.payment.toPay.amount, 0)
      .toFixed(2),
    currency: selected[0]?.payment.toPay.currency || null,
    loyaltyAmount: +selected
      .reduce(
        (acc, item) =>
          acc +
          item.payment.loyalty.fiat /
            (item.payment.loyalty.currencyExchangeRate || 1),

        0,
      )
      .toFixed(2),
  };
};

export function download(url: string, filename?: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename?.replaceAll('.', '') || url.replace(/[|]|(|)/gi, '');
  document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
  a.click();
  a.remove();
}

export type ChildInfo = {
  age: number;
  additionalBed: boolean;
};

export function calculatePrice(
  guests: {
    adults: number;
    children: number;
    childrenInfo: ChildInfo[];
  },
  price: {
    price: IOnlineTourPriceResult;
    childrenPrices: IOnlineTourPriceResult[];
    bedType: string;
  },
  params: { allowSharedRooming: boolean },
) {
  const totalBedCount =
    guests.adults + guests.childrenInfo.filter((ci) => ci.additionalBed).length;
  const bedCapacity = {
    SGL: 1,
    DBL: 2,
    TWN: 2,
    TRP: 3,
    QDR: 4,
  }[price.price.bedType.slice(0, 3)] as number;
  const extraBeds = bedCapacity - totalBedCount;
  const fitting = params.allowSharedRooming
    ? bedCapacity >= totalBedCount
    : bedCapacity === totalBedCount;
  // adult price
  const adultPrice = price.price.price * guests.adults;
  let childrenPrice = 0;
  // children price
  const childrenPrices = guests.childrenInfo
    .map((ci) => {
      const p = price.childrenPrices.find((p) => {
        const hasBed = p.bedStatus === 'Yataklı';
        const needsBed = ci.additionalBed;
        return p.startAgeInMonth <= ci.age * 12 && needsBed === hasBed;
      });
      if (p) {
        childrenPrice += p.price;
      }
      return p;
    })
    .filter((p) => !!p);
  const priceOk =
    adultPrice > 0 && guests.children > 0
      ? childrenPrices.length === guests.children
      : true;

  return {
    price,
    adultPricePerPerson: price.price.price,
    fitting,
    priceOk,
    adultPrice,
    childrenPrice,
    childrenPrices,
    extraBeds,
  };
}

export function groupPricesWithAgeGroups(prices: IOnlineTourPriceResult[]) {
  const adultPrices =
    prices
      ?.filter((price) => price.startAgeInMonth >= adultAgeInMonths)
      .map((p) => ({
        price: p,
        childrenPrices: [] as IOnlineTourPriceResult[],
        bedType: p.bedType,
      })) || [];

  prices?.forEach((price) => {
    if (price.startAgeInMonth >= adultAgeInMonths) return;
    const adultPrice = adultPrices.find((ap) => ap.bedType === price.bedType);
    if (!adultPrice) return;
    adultPrice.childrenPrices.push(price);
    adultPrice.childrenPrices.sort(
      (a, b) => b.startAgeInMonth - a.startAgeInMonth,
    );
  });

  return adultPrices;
}

export function getTourParam(
  data: IOnlineTourSearchResult,
  param: TourParameter,
) {
  return data.parameters.find((p) => p.parameter === param)?.value;
}
